/**
 * Recurring Tickets — form section injected into `CreateTicketDrawer` (Phase 5).
 *
 * Renders the "Select frequency" section at the END of the existing ticket form.
 *
 * Fields (matching design screenshot):
 *  1. Repeat every (frequency) — Select, all 7 values per Gate 2 §8.
 *  2. Creation time — TimePicker HH:MM.
 *  3. Start date — DatePicker, min today (TC-049).
 *  4. End date — DatePicker, optional, must be after start date.
 *
 * When opened from service-desk "Create Ticket" → shows a "Make Recurring" toggle.
 * When opened from control-center/recurring-tickets → toggle is hidden (lockToggle=true),
 * recurring fields always visible.
 *
 * Toggle reset semantics (Gate 2 §9):
 *  - Switching OFF → ON: preserve regular ticket fields, reset recurring fields.
 *  - Switching ON → OFF: preserve regular ticket fields.
 */

import { Stack, Typography, useTheme } from "@mui/material";
import { DateInput, SelectField, TimeField } from "components";
import { ToggleButton } from "components/toggle";
import { useGlobalStore } from "controller";
import dayjs, { type Dayjs } from "dayjs";
import { Controller, useFormContext } from "react-hook-form";
import {
  addMixpanelEvent,
  convertUTCtoTimezoneV2,
  dateTimeToUtcV2,
} from "utilz/helpers";

import { frequencyOptions } from "../utils/frequencyLabels";
import { RECURRING_FIELD_KEYS } from "../validation/recurringTicketValidation";

interface RecurringTicketFormSectionProps {
  lockToggle?: boolean;
  /**
   * Layout variant:
   * - "card" (default): existing Control Center design — 2-column grid inside a card.
   * - "inline": sidebar variant — vertical single-column fields, simple toggle header.
   */
  variant?: "card" | "inline";
}

export const RecurringTicketFormSection = ({
  lockToggle = false,
  variant = "card",
}: RecurringTicketFormSectionProps) => {
  const theme = useTheme();
  const { control, watch, setValue, getValues, formState } = useFormContext();
  // Recurring schedules belong to the company, so we anchor every conversion
  // to the MSP timezone (matches the canonical date-input pattern used in
  // ContractInfoSection / RecurringInvoiceDetailsSection).
  const companyTimezone = useGlobalStore((state) => state.mspTimezone);

  const makeRecurring: boolean = watch("makeRecurring") ?? false;

  const handleToggleChange = (next: boolean) => {
    // Gate 2 §9: on toggle flip, reset the recurring fields to defaults.
    // Regular ticket fields are intentionally untouched.
    setValue("makeRecurring", next, {
      shouldValidate: true,
      shouldDirty: true,
    });
    if (!next) {
      RECURRING_FIELD_KEYS.forEach((key) => {
        setValue(key, "", { shouldValidate: false });
      });
    } else {
      addMixpanelEvent({ event: "recurring_enabled_on_ticket_creation" });
    }
  };

  // When lockToggle is true (opened from recurring-tickets page), always show
  // the frequency fields — no toggle needed.
  const showFields = lockToggle ? true : makeRecurring;
  const isInline = variant === "inline";

  // Field renderers — shared between layouts, only the wrapping Stack differs.
  const frequencyError = formState.errors.recurring_frequency as
    | { message?: string }
    | undefined;
  const frequencyField = (
    <SelectField
      name="recurring_frequency"
      control={control}
      label="Frequency"
      options={frequencyOptions}
      labelMappingKey="label"
      valueMappingKey="value"
      hideHelperBox={isInline}
      required
      error={!!frequencyError}
      errorMessage={frequencyError?.message}
    />
  );

  const creationTimeField = (
    <Controller
      control={control}
      name="recurring_creation_time"
      render={({ field, fieldState }) => (
        <TimeField
          label="Create ticket at *"
          // creation_time is a wall-clock "HH:mm" string interpreted server-side
          // against the MSP tz (sent as the `time_zone` payload field). Anchor
          // the picker AND the display Dayjs to MSP tz so wall-clock values
          // round-trip consistently regardless of viewer location.
          timezone={companyTimezone}
          value={
            field.value
              ? dayjs.tz(
                  `${dayjs().tz(companyTimezone).format("YYYY-MM-DD")}T${field.value}`,
                  companyTimezone,
                )
              : null
          }
          onChangeHandler={(value) => {
            if (!value) {
              field.onChange("");
              return;
            }
            const d = dayjs(value as Dayjs | string).tz(companyTimezone);
            field.onChange(d.isValid() ? d.format("HH:mm") : "");
          }}
          error={!!fieldState.error}
          errorMessage={fieldState.error?.message}
        />
      )}
    />
  );

  const startDateField = (
    <Controller
      control={control}
      name="recurring_start_date"
      render={({ field, fieldState }) => (
        <DateInput
          label="Starts on *"
          // Anchor the picker to MSP tz so "today" highlight + disablePast match
          // the yup "today" validation (both are MSP-anchored). Without this,
          // browser-local "today" can disagree with MSP "today" near midnight.
          timezone={companyTimezone}
          value={convertUTCtoTimezoneV2(field.value, companyTimezone)}
          onChangeHandler={(value) => {
            if (!value) {
              field.onChange("");
              return;
            }
            // Backend contract: end-of-day in MSP tz. Snap before serializing.
            const eod = dayjs(value as Dayjs | string)
              .tz(companyTimezone)
              .endOf("day");
            field.onChange(
              eod.isValid() ? dateTimeToUtcV2(eod, companyTimezone) : "",
            );
          }}
          error={!!fieldState.error}
          errorMessage={fieldState.error?.message}
          disablePast
          fullWidth
          hideHelperBox={isInline}
        />
      )}
    />
  );

  const endDateField = (
    <Controller
      control={control}
      name="recurring_end_date"
      render={({ field, fieldState }) => (
        <DateInput
          label="Ends on"
          timezone={companyTimezone}
          value={convertUTCtoTimezoneV2(field.value, companyTimezone)}
          onChangeHandler={(value) => {
            if (!value) {
              field.onChange(null);
              return;
            }
            const eod = dayjs(value as Dayjs | string)
              .tz(companyTimezone)
              .endOf("day");
            field.onChange(
              eod.isValid() ? dateTimeToUtcV2(eod, companyTimezone) : null,
            );
          }}
          error={!!fieldState.error}
          errorMessage={fieldState.error?.message}
          minDate={convertUTCtoTimezoneV2(
            getValues("recurring_start_date"),
            companyTimezone,
          )}
          fullWidth
          hideHelperBox={isInline}
        />
      )}
    />
  );

  // ----- INLINE VARIANT (sidebar) -----
  if (isInline) {
    return (
      <Stack
        component="section"
        aria-label="Recurring schedule"
        sx={{ gap: "24px" }}
      >
        {/* Enable recurring toggle row */}
        {!lockToggle ? (
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography
              variant="subtitle1"
              sx={{ color: theme.palette.lightBg.high }}
            >
              Make this ticket recurring
            </Typography>
            <ToggleButton
              isChecked={makeRecurring}
              isDisabled={false}
              onChange={(checked: boolean) => handleToggleChange(checked)}
            />
          </Stack>
        ) : null}

        {showFields ? (
          <Stack direction="column" gap="16px">
            {frequencyField}
            {creationTimeField}
            {startDateField}
            {endDateField}
          </Stack>
        ) : null}
      </Stack>
    );
  }

  // ----- CARD VARIANT (default / Control Center) -----
  return (
    <Stack
      component="section"
      aria-label="Recurring schedule"
      sx={{
        gap: "8px",
        pb: "8px",
      }}
    >
      {showFields ? (
        <Stack
          id="recurring-fields-container"
          gap="16px"
          sx={{
            borderRadius: "8px",
            p: "16px",
            backgroundColor: theme.palette.neutral.main,
          }}
        >
          <Typography
            variant="subtitle1"
            sx={{ color: theme.palette.lightBg.high, fontWeight: 600 }}
          >
            Select frequency
          </Typography>

          <Stack direction="row" gap="12px" sx={{ "& > *": { flex: 1 } }}>
            {frequencyField}
            {creationTimeField}
          </Stack>

          <Stack direction="row" gap="12px" sx={{ "& > *": { flex: 1 } }}>
            {startDateField}
            {endDateField}
          </Stack>
        </Stack>
      ) : null}
    </Stack>
  );
};
